import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { ColunaTabelaAcao, FiltrosTabela, TabelaAcao } from '@/components/common/TabelaAcao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { curadoriaService } from '@/services/curadoriaService'
import { usuarioService } from '@/services/usuarioService'
import { authStore } from '@/store/authStore'
import { papelPrincipal } from '@/components/layout/menu'
import {
  CuradoriaFila,
  CuradoriaReserva,
  DecisaoCuradoria,
  DECISAO_CURADORIA_LABEL,
  STATUS_CURADORIA_COR,
  STATUS_CURADORIA_LABEL,
} from '@/types/curadoria'
import { UsuarioAdmin } from '@/types/usuario'
import { CANAL_LABEL, CanalComercial, STATUS_LABEL } from '@/types/statusReserva'
import { formatCNPJ, formatCurrency, formatDate } from '@/utils/formatters'
import { ChipRotaOperacao } from '@/components/elegibilidade/ChipElegibilidade'
import { ChipsValidacaoDocumental } from '@/components/curadoria/ChipsValidacaoDocumental'
import SecaoElegibilidadeReserva from '@/components/reservas/SecaoElegibilidadeReserva'
import FilaGrandesOperacoes from './FilaGrandesOperacoes'

/**
 * Reservas e Curadoria (Onda 6 / A11 — Figma "Reservas e Curadoria", back-office).
 *
 * Fonte única: `GET /v1/oportunidades/curadoria` — KPIs E fila vêm da MESMA resposta,
 * já no escopo do token (OPERADOR do tenant vê o tenant; ADMIN/GERENTE o global). O
 * `statusCuradoria` é DERIVADO no backend por requisição (duplicidade por CNPJ, checklist
 * obrigatório) — nada novo é persistido; a decisão usa só as transições que já existem
 * (`POST /{id}/curadoria/decisao`: APROVAR → APROVADA_ORIGINEI, SOLICITAR_COMPLEMENTO →
 * COM_PENDENCIA + pendência ao consultor, REPROVAR → REPROVADA_ORIGINEI).
 *
 * "Registrar reserva" em nome de um consultor: na UI só ADMIN (R01 — decisão do
 * coordenador): o autocomplete usa `GET /v1/usuarios`, que é ADMIN-only no auth, então um
 * GERENTE veria o botão e nunca acharia consultor. O backend continua aceitando
 * ADMIN/GERENTE (`ehGestor`); GERENTE volta à UI quando `GET /v1/usuarios` abrir para
 * GERENTE (pergunta ao negócio). Escolhe o consultor aqui e segue para a Nova
 * oportunidade em `modoAdmin` (403 a qualquer outro perfil que envie `consultorId`).
 */

const rotuloCanal = (canal: string | null): string =>
  canal ? (CANAL_LABEL[canal as CanalComercial] ?? canal) : '—'

const rotuloAcao = (r: CuradoriaReserva): string =>
  r.statusCuradoria === 'RASCUNHO' ? 'Acompanhar →' : r.statusCuradoria === 'ELEGIVEL' ? 'Liberar →' : 'Revisar →'

const nomeEmpresa = (r: CuradoriaReserva): string =>
  r.razaoSocial || r.nomeFantasia || (r.cnpj ? formatCNPJ(r.cnpj) : 'Empresa sem nome')

const numeroReserva = (r: CuradoriaReserva): string | null =>
  r.numero != null ? `RES-${String(r.numero).padStart(4, '0')}` : null

/**
 * Colunas da aba Curadoria, com filtros de coluna (só escolhem linhas já carregadas — a fila não
 * é paginada; opções = valores presentes nas linhas). "Consultor responsável" só na visão ADMIN.
 */
const colunasCuradoria = (isAdmin: boolean): ColunaTabelaAcao<CuradoriaReserva>[] => [
  {
    titulo: 'Empresa',
    // Nome, CNPJ (cru e com máscara — o filtro também casa só pelos dígitos) e número RES.
    filtro: {
      tipo: 'texto',
      placeholder: 'Nome, CNPJ ou RES-0000',
      textos: (r) => [nomeEmpresa(r), r.razaoSocial, r.nomeFantasia, r.cnpj, r.cnpj ? formatCNPJ(r.cnpj) : null, numeroReserva(r)],
    },
    render: (r) => (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {nomeEmpresa(r)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {r.cnpj ? formatCNPJ(r.cnpj) : '—'}
          {numeroReserva(r) ? ` · ${numeroReserva(r)}` : ''}
        </Typography>
      </Box>
    ),
  },
  {
    titulo: 'Canal',
    filtro: { tipo: 'selecao', opcoes: (r) => [{ valor: r.canal ?? '', rotulo: rotuloCanal(r.canal) }] },
    render: (r) => rotuloCanal(r.canal),
  },
  ...(isAdmin
    ? [
        {
          titulo: 'Consultor responsável',
          chave: 'consultor',
          filtro: {
            tipo: 'selecao' as const,
            // Por id (homônimos não se fundem); sem nome na projeção → uma opção "—" só.
            opcoes: (r: CuradoriaReserva) => [
              r.consultorNome
                ? { valor: `id:${r.usuarioResponsavel ?? ''}`, rotulo: r.consultorNome }
                : { valor: 'sem-nome', rotulo: '— (sem nome)' },
            ],
          },
          render: (r: CuradoriaReserva) => r.consultorNome || '—',
        },
      ]
    : []),
  {
    titulo: 'Produto',
    filtro: {
      tipo: 'selecao',
      opcoes: (r) => [{ valor: r.tipoNecessidade ?? '', rotulo: r.tipoNecessidade ?? '—' }],
    },
    render: (r) => r.tipoNecessidade ?? '—',
  },
  {
    titulo: 'Valor',
    align: 'right',
    filtro: { tipo: 'faixaNumero', valor: (r) => r.valorEstimado },
    render: (r) => (r.valorEstimado != null ? formatCurrency(r.valorEstimado) : '—'),
  },
  {
    titulo: 'Status',
    // Status de curadoria (derivado no backend) E status da reserva: OU dentro do grupo, E entre os grupos.
    filtro: {
      tipo: 'selecao',
      opcoes: (r) => [
        { valor: `curadoria:${r.statusCuradoria}`, rotulo: STATUS_CURADORIA_LABEL[r.statusCuradoria], grupo: 'Curadoria' },
        { valor: `status:${r.status}`, rotulo: STATUS_LABEL[r.status] ?? r.status, grupo: 'Reserva' },
      ],
    },
    render: (r) => (
      <Stack direction="row" spacing={0.5} flexWrap="wrap">
        <Chip
          size="small"
          color={STATUS_CURADORIA_COR[r.statusCuradoria]}
          label={STATUS_CURADORIA_LABEL[r.statusCuradoria]}
        />
        <Chip size="small" variant="outlined" label={STATUS_LABEL[r.status] ?? r.status} />
        <ChipsElegibilidadeLinha r={r} />
        {/* P3: itens aguardando validação / recusados (contados no backend). */}
        <ChipsValidacaoDocumental r={r} />
      </Stack>
    ),
  },
  {
    titulo: 'Validade',
    filtro: { tipo: 'faixaData', valor: (r) => r.prazoReserva },
    render: (r) => (r.prazoReserva ? formatDate(r.prazoReserva) : '—'),
  },
]

/**
 * P2 (AN-PP2 §6): a linha tem alerta quando o SERVIDOR diz que a última avaliação impede o
 * encaminhamento (`alertaElegibilidade`) — nada é avaliado aqui. Antes do Encaminhamento é só
 * informação: a decisão da Curadoria não é bloqueada por isso.
 */
const temAlertaElegibilidade = (r: CuradoriaReserva): boolean => r.alertaElegibilidade === true

const ChipsElegibilidadeLinha: FC<{ r: CuradoriaReserva }> = ({ r }) => (
  <>
    <ChipRotaOperacao rota={r.rotaOperacao} />
    {temAlertaElegibilidade(r) && (
      <Chip size="small" color="warning" variant="outlined" label="Alerta de elegibilidade" />
    )}
  </>
)

const ReservasCuradoriaPage: FC = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const usuario = authStore((s) => s.usuario)
  const papel = papelPrincipal(usuario?.permissoes)
  const backoffice = papel === 'ADMIN' || papel === 'GERENTE' || papel === 'OPERADOR'
  // Só o botão/fluxo "Registrar reserva" é ADMIN-only (GET /v1/usuarios é ADMIN-only no
  // auth); fila e decisão seguem ADMIN/GERENTE/OPERADOR. GERENTE quando GET /v1/usuarios
  // abrir para GERENTE (pergunta ao negócio).
  const isAdmin = papel === 'ADMIN'

  const [fila, setFila] = useState<CuradoriaFila | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  /** P2 (Q67): aba da fila geral ou da fila de Grandes Operações. */
  const [aba, setAba] = useState<'curadoria' | 'grandes-operacoes'>('curadoria')
  const [aberta, setAberta] = useState<CuradoriaReserva | null>(null)
  const [decisao, setDecisao] = useState<DecisaoCuradoria>('APROVAR')
  const [motivo, setMotivo] = useState('')
  const [pendDescricao, setPendDescricao] = useState('')
  const [pendDocumento, setPendDocumento] = useState('')
  const [pendPrazo, setPendPrazo] = useState('')
  const [decidindo, setDecidindo] = useState(false)
  const [erroDecisao, setErroDecisao] = useState<string | null>(null)

  const [registrarAberto, setRegistrarAberto] = useState(false)
  const [consultores, setConsultores] = useState<UsuarioAdmin[]>([])
  const [buscaConsultor, setBuscaConsultor] = useState('')
  const [consultorEscolhido, setConsultorEscolhido] = useState<UsuarioAdmin | null>(null)
  const [carregandoConsultores, setCarregandoConsultores] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setFila(await curadoriaService.fila())
    } catch (e) {
      const msg = (e as { message?: string } | null)?.message
      setErro(msg || 'Não foi possível carregar a fila de curadoria.')
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    if (backoffice) void carregar()
  }, [backoffice, carregar])

  // Consultores para "Registrar reserva" — só quando o diálogo abre (ADMIN).
  useEffect(() => {
    if (!registrarAberto) return
    let ativo = true
    setCarregandoConsultores(true)
    usuarioService
      .listar({ search: buscaConsultor || undefined, max: 50 })
      .then((lista) => {
        if (!ativo) return
        setConsultores(
          lista.filter((u) => u.roles?.some((r) => r.toUpperCase() === 'CONSULTOR'))
        )
      })
      .catch(() => {
        if (ativo) setConsultores([])
      })
      .finally(() => {
        if (ativo) setCarregandoConsultores(false)
      })
    return () => {
      ativo = false
    }
  }, [registrarAberto, buscaConsultor])

  const reservas = useMemo(() => fila?.reservas ?? [], [fila])
  /**
   * Filtros de coluna da aba Curadoria: estado local da página (as listas do app não guardam
   * filtro na URL — só aba/deep link), controlado aqui para sobreviver ao recarregamento da fila
   * depois de uma decisão (o spinner desmonta a tabela).
   */
  const [filtros, setFiltros] = useState<FiltrosTabela>({})
  const colunas = useMemo(() => colunasCuradoria(isAdmin), [isAdmin])
  const linhasAberta = useMemo(
    () => (aberta ? reservas.filter((r) => aberta.conflitosCnpj.includes(r.id)) : []),
    [aberta, reservas]
  )

  const abrir = (r: CuradoriaReserva) => {
    setAberta(r)
    setDecisao(r.statusCuradoria === 'ELEGIVEL' ? 'APROVAR' : 'SOLICITAR_COMPLEMENTO')
    setMotivo('')
    setPendDescricao('')
    setPendDocumento('')
    setPendPrazo('')
    setErroDecisao(null)
  }

  const fechar = () => {
    if (!decidindo) setAberta(null)
  }

  const podeDecidir = motivo.trim().length > 0 && !decidindo

  const decidir = async () => {
    if (!aberta || !podeDecidir) return
    setDecidindo(true)
    setErroDecisao(null)
    try {
      await curadoriaService.decidir(aberta.id, {
        decisao,
        motivo: motivo.trim(),
        pendencia:
          decisao === 'SOLICITAR_COMPLEMENTO'
            ? {
                descricao: pendDescricao.trim() || null,
                documentoSolicitado: pendDocumento.trim() || null,
                prazoResposta: pendPrazo || null,
              }
            : null,
      })
      enqueueSnackbar(`${DECISAO_CURADORIA_LABEL[decisao]}: decisão registrada.`, {
        variant: 'success',
      })
      setAberta(null)
      await carregar()
    } catch (e) {
      const msg = (e as { message?: string } | null)?.message
      setErroDecisao(msg || 'Não foi possível registrar a decisão.')
    } finally {
      setDecidindo(false)
    }
  }

  const irParaRegistrar = () => {
    if (!consultorEscolhido) return
    const params = new URLSearchParams({
      consultorId: consultorEscolhido.id,
      consultorNome: consultorEscolhido.nome,
    })
    setRegistrarAberto(false)
    navigate(`/reservas-curadoria/registrar?${params.toString()}`)
  }

  // Consultor não tem curadoria: cai em Minhas Reservas (a rota é do back-office).
  if (!backoffice) return <Navigate to="/reservas" replace />

  const resumo = fila?.resumo

  return (
    <Box>
      <PageHeader
        title="Reservas e Curadoria"
        subtitle="Fila de reservas enviadas pelos consultores — revise elegibilidade, duplicidade e documentação antes de liberar para a estruturação"
        acao={
          isAdmin ? (
            <Button variant="contained" onClick={() => setRegistrarAberto(true)}>
              Registrar reserva
            </Button>
          ) : undefined
        }
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        {/* fonte: GET /v1/oportunidades/curadoria (resumo.*) — mesma resposta da fila */}
        <KpiCard
          label="Novas"
          valor={resumo ? resumo.novas : '—'}
          legenda="enviadas e ainda não abertas"
          variante="escura"
          carregando={carregando}
        />
        <KpiCard
          label="Em curadoria"
          valor={resumo ? resumo.emCuradoria : '—'}
          legenda="em análise ou com pendência"
          carregando={carregando}
        />
        <KpiCard
          label="Liberadas"
          valor={resumo ? resumo.liberadas : '—'}
          legenda={resumo ? `últimos ${resumo.janelaLiberadasDias} dias` : 'para estruturação'}
          carregando={carregando}
        />
        <KpiCard
          label="Exceções"
          valor={resumo ? resumo.excecoes : '—'}
          legenda="renovações aguardando decisão"
          carregando={carregando}
        />
        {/* 09/09: rascunho registrado (pelo consultor ou em nome dele) e ainda não enviado. */}
        <KpiCard
          label="Rascunhos"
          valor={resumo ? resumo.rascunhos : '—'}
          legenda="ainda não enviadas pelo consultor"
          carregando={carregando}
        />
        {/* P2 (Q67): fonte = resumo.grandesOperacoes, count da MESMA query da aba "Grandes Operações". */}
        <KpiCard
          label="Grandes Operações"
          valor={resumo?.grandesOperacoes != null ? resumo.grandesOperacoes : '—'}
          legenda="reservas na fila do back-office"
          carregando={carregando}
        />
      </Box>

      <Tabs
        value={aba}
        onChange={(_, v) => setAba(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        aria-label="Filas do back-office"
      >
        <Tab value="curadoria" label="Curadoria" />
        <Tab
          value="grandes-operacoes"
          label={resumo?.grandesOperacoes != null ? `Grandes Operações (${resumo.grandesOperacoes})` : 'Grandes Operações'}
        />
      </Tabs>

      {aba === 'grandes-operacoes' ? (
        <FilaGrandesOperacoes />
      ) : (
      <>

      {carregando ? (
        <EstadoDeLista estado="carregando" />
      ) : erro ? (
        <EstadoDeLista
          estado="erro"
          mensagem={erro}
          acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
        />
      ) : reservas.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem="Nenhuma reserva aguardando curadoria"
          detalhe="Reservas enviadas pelos consultores aparecem aqui até serem liberadas, devolvidas ou reprovadas."
        />
      ) : (
        <TabelaAcao<CuradoriaReserva>
          aria-label="Fila de curadoria"
          linhas={reservas}
          chaveDaLinha={(r) => r.id}
          onAbrir={abrir}
          renderAcao={rotuloAcao}
          colunas={colunas}
          filtros={filtros}
          onFiltrosChange={setFiltros}
          rotuloContagem={(n, m) => `${n} de ${m} ${m === 1 ? 'reserva' : 'reservas'}`}
          mensagemVazioFiltrado="Nenhuma reserva com esses filtros"
        />
      )}
      </>
      )}

      {/* Drawer "Curadoria da reserva" */}
      <Drawer anchor="right" open={aberta != null} onClose={fechar}>
        {aberta && (
          <Box sx={{ width: { xs: '100vw', sm: 480 }, p: 3 }} role="dialog" aria-label="Curadoria da reserva">
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h5">Curadoria da reserva</Typography>
              <IconButton aria-label="Fechar" onClick={fechar} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>

            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {nomeEmpresa(aberta)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {aberta.cnpj ? formatCNPJ(aberta.cnpj) : '—'} · {rotuloCanal(aberta.canal)} ·{' '}
              {aberta.tipoNecessidade ?? 'Produto não informado'} ·{' '}
              {aberta.valorEstimado != null ? formatCurrency(aberta.valorEstimado) : 'sem valor'}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
              <Chip
                size="small"
                color={STATUS_CURADORIA_COR[aberta.statusCuradoria]}
                label={STATUS_CURADORIA_LABEL[aberta.statusCuradoria]}
              />
              <Chip size="small" variant="outlined" label={STATUS_LABEL[aberta.status] ?? aberta.status} />
              <ChipsElegibilidadeLinha r={aberta} />
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* P2 (AN-PP2 §6/§8): snapshot e rota da reserva — só mostra (alerta não trava a decisão). */}
            <SecaoElegibilidadeReserva oportunidadeId={aberta.id} papel={papel} modo="resumo" />

            <Typography variant="overline">Elegibilidade e documentação</Typography>
            <Typography variant="body2">
              Checklist obrigatório: {aberta.percentualChecklist}% (
              {aberta.documentosObrigatoriosTotal - aberta.documentosObrigatoriosFaltantes} de{' '}
              {aberta.documentosObrigatoriosTotal} documentos)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Validade da reserva: {aberta.prazoReserva ? formatDate(aberta.prazoReserva) : '—'}
            </Typography>

            <Typography variant="overline" sx={{ display: 'block', mt: 2 }}>
              Duplicidades
            </Typography>
            {aberta.conflitosCnpj.length === 0 && aberta.conflitosForaDoEscopo === 0 ? (
              <Typography variant="body2">Nenhuma outra reserva com este CNPJ.</Typography>
            ) : (
              <Stack spacing={0.5}>
                {aberta.conflitosCnpj.map((id) => {
                  const outra = linhasAberta.find((r) => r.id === id)
                  return (
                    <Link key={id} component={RouterLink} to={`/oportunidades/${id}`} variant="body2">
                      {outra
                        ? `RES-${String(outra.numero ?? 0).padStart(4, '0')} · ${STATUS_LABEL[outra.status] ?? outra.status}`
                        : `Reserva ${id.slice(0, 8)}`}
                    </Link>
                  )
                })}
                {aberta.conflitosForaDoEscopo > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    +{aberta.conflitosForaDoEscopo} fora do seu escopo (não listadas)
                  </Typography>
                )}
              </Stack>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Validação completa (06/09): dados, diagnóstico, checklist e os arquivos em si,
                com a aprovação exigindo as duas conferências. A decisão rápida abaixo continua. */}
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate(`/validacao-reservas/${aberta.id}`)}
              sx={{ mb: 2 }}
            >
              Validar dados e documentos →
            </Button>

            {/* Rascunho (09/09): o consultor ainda não enviou — não há decisão a tomar. A
                máquina de status recusa a curadoria antes de ENVIADA; aqui só o acompanhamento. */}
            {aberta.status === 'RASCUNHO' ? (
              <>
                <Alert severity="info">
                  Rascunho: o consultor ainda não enviou esta reserva para aprovação.
                  {aberta.documentosObrigatoriosFaltantes > 0
                    ? ` Faltam ${aberta.documentosObrigatoriosFaltantes} documento(s) obrigatório(s).`
                    : ' Documentos obrigatórios completos.'}{' '}
                  Ela entra na fila de curadoria quando for enviada.
                </Alert>
                {papel === 'ADMIN' && (
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={() => navigate(`/reservas-curadoria/registrar?continuar=${aberta.id}`)}
                  >
                    Continuar cadastro em nome do consultor →
                  </Button>
                )}
              </>
            ) : (
            <>
            <Typography variant="overline">Decisão</Typography>
            <FormControl component="fieldset" sx={{ display: 'block' }}>
              <RadioGroup
                aria-label="Decisão da curadoria"
                value={decisao}
                onChange={(e) => setDecisao(e.target.value as DecisaoCuradoria)}
              >
                {(Object.keys(DECISAO_CURADORIA_LABEL) as DecisaoCuradoria[]).map((d) => (
                  <FormControlLabel
                    key={d}
                    value={d}
                    control={<Radio size="small" />}
                    label={DECISAO_CURADORIA_LABEL[d]}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            <TextField
              label="Motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              required
              fullWidth
              multiline
              minRows={2}
              sx={{ mt: 1 }}
              helperText="Obrigatório em qualquer decisão — fica na atividade da reserva."
            />
            {decisao === 'SOLICITAR_COMPLEMENTO' && (
              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                <TextField
                  label="O que falta"
                  value={pendDescricao}
                  onChange={(e) => setPendDescricao(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Documento solicitado"
                  value={pendDocumento}
                  onChange={(e) => setPendDocumento(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Prazo de resposta"
                  type="date"
                  value={pendPrazo}
                  onChange={(e) => setPendPrazo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
            )}
            {erroDecisao && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {erroDecisao}
              </Alert>
            )}
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button onClick={fechar} disabled={decidindo}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                color={decisao === 'REPROVAR' ? 'error' : 'primary'}
                onClick={() => void decidir()}
                disabled={!podeDecidir}
              >
                {decidindo ? 'Registrando…' : DECISAO_CURADORIA_LABEL[decisao]}
              </Button>
            </Stack>
            </>
            )}
          </Box>
        )}
      </Drawer>

      {/* Registrar reserva em nome de um consultor (ADMIN na UI; ver cabeçalho) */}
      <Dialog open={registrarAberto} onClose={() => setRegistrarAberto(false)} fullWidth maxWidth="sm">
        <DialogTitle>Registrar reserva</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A reserva e a reunião nascem do consultor escolhido; o registro fica em seu nome
            na auditoria.
          </Typography>
          <Autocomplete
            options={consultores}
            value={consultorEscolhido}
            onChange={(_, v) => setConsultorEscolhido(v)}
            onInputChange={(_, v) => setBuscaConsultor(v)}
            getOptionLabel={(u) => `${u.nome} (${u.email})`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loading={carregandoConsultores}
            noOptionsText="Nenhum consultor encontrado"
            renderInput={(params) => <TextField {...params} label="Consultor" required />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegistrarAberto(false)}>Cancelar</Button>
          <Button variant="contained" onClick={irParaRegistrar} disabled={!consultorEscolhido}>
            Continuar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ReservasCuradoriaPage
